import gql from "graphql-tag";
import * as React from "react";
import {useQuery} from "react-apollo";
import {useParams} from "react-router-dom";
import get from "lodash/get";
import useErrorReporting from "shared/General/components/ErrorReporting/useErrorReporting";

const SLUG_ERROR_MESSAGE =
    "GraphQL error: 404 Not Found: The requested URL was not found on the server. If you entered the URL manually please check your spelling and try again.";

export const GetUniversityQuery = gql`
    query GetUniversityQuery($universitySlug: String) {
        university(slug: $universitySlug) {
            id
            name
            colour
        }
    }
`;

const UniversityContext = React.createContext({
    name: "",
    id: "",
    slug: "",
    color: "",
});

export const UniversityProvider = ({children}) => {
    const {universitySlug} = useParams();
    const {reportError} = useErrorReporting();
    const {data, loading, error} = useQuery(GetUniversityQuery, {
        variables: {
            universitySlug,
        },
    });

    if (loading) {
        return <p>Loading...</p>;
    }

    if (!data || error) {
        if (error && error.message === SLUG_ERROR_MESSAGE) {
            reportError(error, {
                tags: {universitySlug, invalidSlug: true},
            });
            return (
                <p>
                    An error occurred. Could not get the university details due to an incorrect
                    university slug. Please make sure that the widget has been embedded correctly.
                </p>
            );
        }
        if (error) {
            reportError(error);
        }
        return <p>An error occurred. Please try again later.</p>;
    }

    const value = {
        slug: universitySlug ?? "not-found-university-slug",
        id: get(data, "university.id", "not-found-university-id"),
        name: get(data, "university.name", "not-found-university-name"),
        color: get(data, "university.colour", undefined),
    };

    return <UniversityContext.Provider value={value}>{children}</UniversityContext.Provider>;
};

export const useUniversity = () => React.useContext(UniversityContext);
